import {
  useState,
} from 'react';
import {
  Callout,
  Field,
  Modal,
  TextInput,
  explainFailure,
  isApiClientError,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  pendingBalanceLines,
} from '../../screens.ts';
import { t, tCount } from '../../messages.ts';

/**
 * Item 7 (third batch) — AMS's own entry point onto the same ACTIVE<->
 * DEACTIVATED toggle an operator's own staff already has (DisableAffiliate,
 * affdice-admin's own App.tsx). Not `Confirm`: a deactivation with a payable
 * balance refuses with PENDING_BALANCE and asks for a forced override plus a
 * reason on record (§6.9), which needs its own state machine — the same
 * shape affdice-admin's own modal already uses for the same refusal.
 */
export function AffiliateStatusModal({
  api,
  operatorId,
  affiliateId,
  name,
  from,
  onClose,
  onDone,
}: {
  api: PlatformApi;
  operatorId: string;
  affiliateId: string;
  name: string;
  from: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const to = from === 'ACTIVE' ? 'DEACTIVATED' : 'ACTIVE';
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [blocked, setBlocked] = useState<ReturnType<typeof pendingBalanceLines> | null>(null);
  const [force, setForce] = useState(false);
  const [justification, setJustification] = useState('');

  const submit = (withForce: boolean) => {
    setBusy(true);
    setFailure(null);
    api
      .changeAffiliateStatus(operatorId, affiliateId, {
        to,
        reasonCode: reason,
        ...(withForce ? { force: true, justification } : {}),
      })
      .then(onDone)
      .catch((e: unknown) => {
        if (isApiClientError(e) && e.code === 'PENDING_BALANCE') {
          const balances = (e.details?.['balances'] ?? []) as Parameters<typeof pendingBalanceLines>[0];
          setBlocked(pendingBalanceLines(balances));
        } else {
          setFailure(e);
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal title={to === 'DEACTIVATED' ? t('aff.status.deactivateTitle', { name }) : t('aff.status.reactivateTitle', { name })} onClose={onClose}>
      <div className="form">
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        {/*
            Scope first, consequence second.

            This popup and the per-brand one below it look alike and read
            alike, and this is the wider of the two: it reaches the affiliate
            across every brand they are enrolled on here, not the one brand
            whose row was pressed. That was nowhere on the form — the sentence
            began at "links stop accepting new clicks", which is equally true
            of the narrow action — so the two were distinguishable only by
            which chip you remembered pressing.

            It stops at this operator. The scopes that reach further — chosen
            brands or operators across every profile this person holds, or
            all of them — are behind `Suspend…`, which stays its own button.
        */}
        <Callout tone={to === 'DEACTIVATED' ? 'caution' : 'note'}>
          {to === 'DEACTIVATED'
            ? t('aff.status.deactivateNote')
            : t('aff.status.reactivateNote')}
        </Callout>
        <Field label={t('common.reason')}>
          <TextInput value={reason} onChange={setReason} />
        </Field>

        {blocked ? (
          <>
            <Callout tone="caution">
              {tCount('aff.status.blocked', blocked.length)}
              <ul>
                {blocked.map((b) => (
                  <li key={b.brandId}>{b.brand}: {b.amount}</li>
                ))}
              </ul>
            </Callout>
            <Field group label={t('aff.status.forceGroup')}>
              <label className="checkbox-stack-row">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                {' '}{t('aff.status.forceCheck')}
              </label>
            </Field>
            {force ? (
              <Field label={t('aff.status.why')}>
                <TextInput value={justification} onChange={setJustification} />
              </Field>
            ) : null}
          </>
        ) : null}

        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={onClose}>{t('common.cancel')}</button>
          <button
            type="button"
            className={to === 'DEACTIVATED' ? 'danger' : 'primary'}
            disabled={busy || !reason.trim() || (blocked !== null && force && !justification.trim())}
            onClick={() => submit(blocked !== null && force)}
          >
            {busy ? t('aff.status.working') : to === 'DEACTIVATED' ? t('aff.status.deactivate') : t('aff.status.reactivate')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
